import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  PiggyBank, 
  TrendingUp, 
  Shield, 
  DollarSign, 
  Calendar,
  MoreVertical,
  Trash2,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Plus,
  BarChart3
} from 'lucide-react'
import { AIBudget, BudgetCategory } from '../../types/ai-budget'
import { useBudgetTransactions } from '../../hooks/useBudgetTransactions'

interface BudgetListProps {
  budgets: AIBudget[]
  loading: boolean
  error: string | null
  onDeleteBudget: (budgetId: string) => Promise<void>
  onUpdateStatus: (budgetId: string, status: 'active' | 'paused' | 'completed') => Promise<void>
  onRefresh: () => Promise<void>
  className?: string
}

const BudgetList: React.FC<BudgetListProps> = ({
  budgets,
  loading,
  error,
  onDeleteBudget,
  onUpdateStatus,
  onRefresh,
  className = ''
}) => {
  const [openMenus, setOpenMenus] = useState<string[]>([])
  const [expandedBudgets, setExpandedBudgets] = useState<string[]>([])
  const [, setSelectedBudget] = useState<string | null>(null)
  
  // Get budget transaction data for the selected budget
  const { 
    getCategoryProgress, 
    getTransactionsForCategory
  } = useBudgetTransactions()

  const toggleMenu = (budgetId: string) => {
    setOpenMenus(prev => 
      prev.includes(budgetId) 
        ? prev.filter(id => id !== budgetId)
        : [...prev, budgetId]
    )
  }

  const toggleExpanded = (budgetId: string) => {
    setExpandedBudgets(prev =>
      prev.includes(budgetId)
        ? prev.filter(id => id !== budgetId)
        : [...prev, budgetId]
    )
  }

  const getStatusIcon = (status: AIBudget['status']) => {
    switch (status) {
      case 'active': return <Play className="w-4 h-4 text-green-500" />
      case 'paused': return <Pause className="w-4 h-4 text-yellow-500" />
      case 'completed': return <CheckCircle className="w-4 h-4 text-blue-500" />
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: AIBudget['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200'
      case 'paused': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getCategoryIcon = (type: BudgetCategory['type']) => {
    switch (type) {
      case 'essential': return <Shield className="w-4 h-4 text-blue-500" />
      case 'savings': return <PiggyBank className="w-4 h-4 text-green-500" />
      case 'investment': return <TrendingUp className="w-4 h-4 text-purple-500" />
      default: return <DollarSign className="w-4 h-4 text-gray-500" />
    }
  }

  const handleDeleteBudget = async (budgetId: string) => {
    if (confirm('Are you sure you want to delete this budget? This action cannot be undone.')) {
      try {
        await onDeleteBudget(budgetId)
        setOpenMenus(prev => prev.filter(id => id !== budgetId))
      } catch (error) {
        console.error('Failed to delete budget:', error)
        alert('Failed to delete budget. Please try again.')
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className={`bg-background-800 rounded-xl border border-background-700 p-8 ${className}`}>
        <div className="flex items-center justify-center space-x-2 text-background-400">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-5 h-5 border-2 border-background-600 border-t-accent-500 rounded-full"
          />
          <span>Loading your budgets...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-background-800 border border-red-500/20 rounded-xl p-6 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <div>
              <h3 className="font-medium text-background-100">Error Loading Budgets</h3>
              <p className="text-sm text-background-400 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="px-3 py-1 text-sm bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (budgets.length === 0) {
    return (
      <div className={`bg-background-800 border border-background-700 rounded-xl p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-background-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <PiggyBank className="w-8 h-8 text-background-400" />
        </div>
        <h3 className="text-lg font-medium text-background-100 mb-2">No Budgets Yet</h3>
        <p className="text-background-400 mb-4">
          Create your first budget by chatting with Kubera about your finances.
        </p>
        <div className="flex items-center justify-center space-x-2 text-sm text-background-500">
          <Plus className="w-4 h-4" />
          <span>Start a conversation to get budget recommendations</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-background-100">Your Budgets ({budgets.length})</h3>
        <button
          onClick={onRefresh}
          className="text-sm text-accent-400 hover:text-accent-300 font-medium transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {budgets.map((budget) => (
            <motion.div
              key={budget.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-background-800 border border-background-700 rounded-xl p-4 hover:bg-background-750 transition-all"
            >
              {/* Budget Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-background-100">{budget.name}</h4>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center space-x-1 ${getStatusColor(budget.status)}`}>
                      {getStatusIcon(budget.status)}
                      <span className="capitalize">{budget.status}</span>
                    </div>
                  </div>
                  
                  {budget.description && (
                    <p className="text-sm text-background-400 mb-2">{budget.description}</p>
                  )}

                  <div className="flex items-center space-x-4 text-sm text-background-500">
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-3 h-3" />
                      <span>${budget.monthly_income.toLocaleString()}/month</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>Created {formatDate(budget.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <button
                    onClick={() => toggleMenu(budget.id)}
                    className="p-1 hover:bg-background-700 rounded-lg transition-colors"
                  >
                    <MoreVertical className="w-4 h-4 text-background-400" />
                  </button>

                  {openMenus.includes(budget.id) && (
                    <div className="absolute right-0 top-8 bg-background-900 border border-background-700 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                      <button
                        onClick={() => {
                          const newStatus = budget.status === 'active' ? 'paused' : 'active'
                          onUpdateStatus(budget.id, newStatus)
                          setOpenMenus(prev => prev.filter(id => id !== budget.id))
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-background-800 flex items-center space-x-2 text-background-300 hover:text-background-100"
                      >
                        {budget.status === 'active' ? (
                          <>
                            <Pause className="w-4 h-4" />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            <span>Activate</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          onUpdateStatus(budget.id, 'completed')
                          setOpenMenus(prev => prev.filter(id => id !== budget.id))
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-background-800 flex items-center space-x-2 text-background-300 hover:text-background-100"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Complete</span>
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={() => handleDeleteBudget(budget.id)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-red-500/10 text-red-400 hover:text-red-300 flex items-center space-x-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Budget Summary */}
              <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-background-700">
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-400">
                    ${budget.savings_goal.toLocaleString()}
                  </div>
                  <div className="text-xs text-background-500">Savings Goal</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-purple-400">
                    ${budget.investment_goal.toLocaleString()}
                  </div>
                  <div className="text-xs text-background-500">Investment Goal</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-background-100">
                    {Math.round(((budget.savings_goal + budget.investment_goal) / budget.monthly_income) * 100)}%
                  </div>
                  <div className="text-xs text-background-500">Savings Rate</div>
                </div>
              </div>

              {/* Expandable Categories */}
              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleExpanded(budget.id)}
                    className="text-sm text-accent-400 hover:text-accent-300 font-medium transition-colors"
                  >
                    {expandedBudgets.includes(budget.id) ? 'Hide Details' : 'View Categories'}
                  </button>
                  
                  {budget.status === 'active' && (
                    <button
                      onClick={() => setSelectedBudget(budget.id)}
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors flex items-center space-x-1"
                    >
                      <BarChart3 className="w-3 h-3" />
                      <span>Track Progress</span>
                    </button>
                  )}
                </div>

                <AnimatePresence>
                  {expandedBudgets.includes(budget.id) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden mt-3"
                    >
                      <div className="space-y-2 p-3 bg-background-750 rounded-lg">
                        {budget.budget_categories.map((category, index) => {
                          // Get progress data for this category if budget is active
                          const progress = budget.status === 'active' ? getCategoryProgress(category.name) : null
                          const transactions = budget.status === 'active' ? getTransactionsForCategory(category.name) : []
                          
                          return (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {getCategoryIcon(category.type)}
                                  <span className="text-sm font-medium text-background-100">
                                    {category.name}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-background-200">
                                    ${category.allocated.toLocaleString()}
                                  </span>
                                  <span className="text-xs text-background-400">
                                    ({category.percentage}%)
                                  </span>
                                </div>
                              </div>
                              
                              {/* Progress bar for active budgets */}
                              {budget.status === 'active' && progress && (
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-background-500">
                                      Spent: ${progress.spent.toLocaleString()}
                                    </span>
                                    <span className={`font-medium ${
                                      progress.percentage >= 95 ? 'text-red-400' :
                                      progress.percentage >= 75 ? 'text-yellow-400' :
                                      'text-green-400'
                                    }`}>
                                      {progress.percentage}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-background-700 rounded-full h-1.5">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{ width: `${Math.min(progress.percentage, 100)}%` }}
                                      transition={{ duration: 0.6, ease: "easeOut" }}
                                      className={`h-1.5 rounded-full ${
                                        progress.percentage >= 95 ? 'bg-red-500' :
                                        progress.percentage >= 75 ? 'bg-yellow-500' :
                                        'bg-green-500'
                                      }`}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-background-500">
                                    <span>
                                      {progress.remaining >= 0 ? (
                                        <>
                                          <CheckCircle className="w-3 h-3 inline mr-1 text-green-500" />
                                          ${progress.remaining.toLocaleString()} left
                                        </>
                                      ) : (
                                        <>
                                          <AlertCircle className="w-3 h-3 inline mr-1 text-red-500" />
                                          ${Math.abs(progress.remaining).toLocaleString()} over
                                        </>
                                      )}
                                    </span>
                                    <span>{transactions.length} transactions</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default BudgetList
